import {createApi} from "@reduxjs/toolkit/query/react"
import baseQuery from "../../utils/apiIIKOConfig"
import {IBasicParams, IBasicResponse} from "../../types/api"
import {
    ICategoryUpdateParams,
    ICategoryRecommendation,
    ICategoryRecommendationCreateParams,
    ICategoryRecommendationDeleteParams,
    ICategoryRecommendationProductPositionUpdateParams,
    ICategoryRecommendationUpdateParams,
    ICombo,
    IComboByIdParams,
    IComboCommon,
    IComboItemProductPositionUpdateParams,
    IComboItemProductUpdateParams,
    IComboItemUpdateParams,
    IComboListParams,
    IComboUpdateParams,
    IMenuParams,
    IMenusResponse,
    IModifierUpdateParams,
    IModifierGroupUpdateParams,
    IPositionUpdateResponse,
    IProductGiftableUpdateParams,
    IProductPacketUpdateParams,
    IProductPositionUpdateParams,
    IProductServiceFeeUpdateParams,
    IProductShippingUpdateParams,
    IProductUpdateParams,
    IProductSizeUpdateParams,
    IStopListItem,
    IStopListParams
} from "./MenuApiTypes"

export const menuApi = createApi({
    reducerPath: "menuApi",
    baseQuery,
    tagTypes: ["menu", "categoryRecommendations"],
    endpoints: build => ({
        getMenu: build.query<IBasicResponse<IMenusResponse>, IMenuParams>({
            query: ({lang, serviceMode}) => ({
                url: "v1/menus",
                method: "GET",
                params: {
                    serviceMode
                },
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["menu"]
        }),
        getMenuList: build.query<IBasicResponse<IMenusResponse>, IBasicParams>({
            query: ({lang}) => ({
                url: "v1/menus/list",
                method: "GET",
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["menu"]
        }),
        syncMenu: build.mutation<IBasicResponse<void>, IMenuParams>({
            query: ({lang}) => ({
                url: "v1/menus/sync",
                method: "POST",
                headers: {
                    "Accept-Language": lang
                }
            }),
            invalidatesTags: ["menu"]
        }),
        syncCdpMenu: build.mutation<IBasicResponse<void>, IBasicParams>({
            query: ({lang}) => ({
                url: "v1/cdp/menus/sync",
                method: "POST",
                headers: {
                    "Accept-Language": lang
                }
            })
        }),
        getCombos: build.query<IBasicResponse<ICombo[]>, IComboListParams>({
            query: ({lang, branch_id, service_mode}) => ({
                url: "v1/combos/list",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                },
                body: {
                    service_mode,
                    ...(branch_id ? {branch_id} : {})
                }
            }),
            providesTags: ["menu"]
        }),
        getComboCommonList: build.query<IBasicResponse<IComboCommon[]>, IBasicParams>({
            query: ({lang}) => ({
                url: "v1/combos/common-list",
                method: "GET",
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["menu"]
        }),
        getComboById: build.query<IBasicResponse<ICombo>, IComboByIdParams>({
            query: ({lang, id}) => ({
                url: `v1/combos/${id}`,
                method: "GET",
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["menu"]
        }),
        syncCombos: build.mutation<IBasicResponse<void>, IMenuParams>({
            query: ({lang}) => ({
                url: "v1/combos/sync",
                method: "POST",
                headers: {
                    "Accept-Language": lang
                }
            }),
            invalidatesTags: ["menu"]
        }),
        getStopList: build.query<IBasicResponse<IStopListItem[]>, IStopListParams>({
            query: ({lang, branchId}) => ({
                url: "v1/stop-list",
                method: "GET",
                params: {
                    branchId
                },
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["menu"]
        }),
        getCategoryRecommendations: build.query<IBasicResponse<ICategoryRecommendation[]>, IBasicParams>({
            query: ({lang}) => ({
                url: "v1/categories/recommendations",
                method: "GET",
                headers: {
                    "Accept-Language": lang
                }
            }),
            providesTags: ["categoryRecommendations"]
        }),
        createCategoryRecommendation: build.mutation<IBasicResponse<number>, ICategoryRecommendationCreateParams>({
            query: ({lang, ...body}) => ({
                url: "v1/categories/recommendations",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategoryRecommendation: build.mutation<IBasicResponse<void>, ICategoryRecommendationUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/categories/recommendations/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategoryRecommendationProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, ICategoryRecommendationProductPositionUpdateParams>({
            query: ({lang, recommendation_id, product_id, position}) => ({
                url: `v1/categories/recommendations/${recommendation_id}/products/${product_id}/positions`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body: {position}
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        deleteCategoryRecommendation: build.mutation<IBasicResponse<void>, ICategoryRecommendationDeleteParams>({
            query: ({lang, id}) => ({
                url: `v1/categories/recommendations/${id}`,
                method: "DELETE",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["categoryRecommendations"]
        }),
        updateCategory: build.mutation<IBasicResponse<void>, ICategoryUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/categories/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateProduct: build.mutation<IBasicResponse<void>, IProductUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/products/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, IProductPositionUpdateParams>({
            query: ({lang, id, position}) => ({
                url: `v1/products/${id}/positions`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body: {position}
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductPacket: build.mutation<IBasicResponse<void>, IProductPacketUpdateParams>({
            query: ({lang, idList}) => ({
                url: "v1/products/packets",
                method: "PATCH",
                params: {
                    idList
                },
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductGiftable: build.mutation<IBasicResponse<void>, IProductGiftableUpdateParams>({
            query: ({lang, idList}) => ({
                url: "v1/products/giftable",
                method: "PATCH",
                params: {
                    idList
                },
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductShipping: build.mutation<IBasicResponse<void>, IProductShippingUpdateParams>({
            query: ({lang, id}) => ({
                url: `v1/products/${id}/shipping`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductServiceFee: build.mutation<IBasicResponse<void>, IProductServiceFeeUpdateParams>({
            query: ({lang, id}) => ({
                url: `v1/products/${id}/service-fee`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent"
                }
            }),
            invalidatesTags: ["menu"]
        }),
        updateProductSize: build.mutation<IBasicResponse<void>, IProductSizeUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/product-sizes/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateModifier: build.mutation<IBasicResponse<void>, IModifierUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/modifiers/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateModifierGroup: build.mutation<IBasicResponse<void>, IModifierGroupUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/modifier-groups/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateCombo: build.mutation<IBasicResponse<void>, IComboUpdateParams>({
            query: ({lang, id, ...body}) => ({
                url: `v1/combos/${id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItem: build.mutation<IBasicResponse<void>, IComboItemUpdateParams>({
            query: ({lang, combo_item_id, ...body}) => ({
                url: `v1/combos/items/${combo_item_id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItemProduct: build.mutation<IBasicResponse<void>, IComboItemProductUpdateParams>({
            query: ({lang, combo_item_id, product_id, ...body}) => ({
                url: `v1/combos/items/${combo_item_id}/products/${product_id}`,
                method: "PUT",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body
            }),
            invalidatesTags: ["menu"]
        }),
        updateComboItemProductPosition: build.mutation<IBasicResponse<IPositionUpdateResponse>, IComboItemProductPositionUpdateParams>({
            query: ({lang, combo_item_id, product_id, position}) => ({
                url: `v1/combos/items/${combo_item_id}/products/${product_id}/positions`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": "Asia/Tashkent",
                },
                body: {position}
            }),
            invalidatesTags: ["menu"]
        })
    })
})

export const {
    useGetMenuQuery,
    useGetMenuListQuery,
    useSyncMenuMutation,
    useSyncCdpMenuMutation,
    useGetCombosQuery,
    useGetComboCommonListQuery,
    useGetComboByIdQuery,
    useLazyGetComboByIdQuery,
    useSyncCombosMutation,
    useGetStopListQuery,
    useGetCategoryRecommendationsQuery,
    useCreateCategoryRecommendationMutation,
    useUpdateCategoryRecommendationMutation,
    useUpdateCategoryRecommendationProductPositionMutation,
    useDeleteCategoryRecommendationMutation,
    useUpdateCategoryMutation,
    useUpdateProductMutation,
    useUpdateProductPositionMutation,
    useUpdateProductPacketMutation,
    useUpdateProductGiftableMutation,
    useUpdateProductShippingMutation,
    useUpdateProductServiceFeeMutation,
    useUpdateProductSizeMutation,
    useUpdateModifierMutation,
    useUpdateModifierGroupMutation,
    useUpdateComboMutation,
    useUpdateComboItemMutation,
    useUpdateComboItemProductMutation,
    useUpdateComboItemProductPositionMutation
} = menuApi
